import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  increment,
} from "../../../config/firebase";
import { ERROR_MESSAGES } from "../types/crmTypes";
import {
  generateClientCode,
  generateLeadCode,
  generateProjectCode,
  generateQuotationNumber,
  generateInvoiceNumber,
  generateContractNumber,
  validateClientData,
  validateLeadData,
  validateProjectData,
} from "../utils/crmHelpers";

class CRMService {
  // ============ CLIENTS ============
  getClientsCollection() {
    return collection(db, "clients");
  }

  async getClients(filters = {}) {
    try {
      let q = query(this.getClientsCollection(), orderBy("createdAt", "desc"));
      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }
      if (filters.type && filters.type !== "all") {
        q = query(q, where("type", "==", filters.type));
      }
      const snapshot = await getDocs(q);
      let clients = [];
      snapshot.forEach((docSnap) => {
        clients.push({
          ...this.convertDocData(docSnap.data()),
          id: docSnap.id,
        });
      });
      clients = clients.filter((c) => c.isArchived !== true);

      if (filters.search) {
        const s = filters.search.toLowerCase();
        clients = clients.filter(
          (c) =>
            c.fullName?.toLowerCase().includes(s) ||
            c.email?.toLowerCase().includes(s) ||
            c.phone?.toLowerCase().includes(s) ||
            c.clientCode?.toLowerCase().includes(s),
        );
      }
      return clients;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getClientById(id) {
    try {
      const docRef = doc(db, "clients", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);
      return { ...this.convertDocData(docSnap.data()), id: docSnap.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createClient(data) {
    try {
      const errors = validateClientData(data);
      if (Object.keys(errors).length > 0)
        throw new Error(Object.values(errors)[0]);

      const clientCode = generateClientCode();
      const clientData = {
        clientCode,
        fullName:
          data.fullName ||
          `${data.firstName || ""} ${data.lastName || ""}`.trim(),
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        displayName: data.displayName || "",
        email: data.email || "",
        phone: data.phone || "",
        alternatePhone: data.alternatePhone || "",
        whatsapp: data.whatsapp || data.phone || "",
        address: data.address || {},
        company: data.company || "",
        type: data.type || "individual",
        status: data.status || "active",
        stage: data.stage || "lead",
        source: {
          primary: data.source?.primary || "website",
          secondary: data.source?.secondary || "",
          campaignId: data.source?.campaignId || "",
          campaignName: data.source?.campaignName || "",
        },
        ownership: {
          assignedTo: data.ownership?.assignedTo || "",
          teamId: data.ownership?.teamId || "",
          branchId: data.ownership?.branchId || "",
        },
        preferences: data.preferences || {},
        financials: {
          budgetRange: data.financials?.budgetRange || {},
          currency: "INR",
          lifetimeValue: 0,
          outstandingAmount: 0,
        },
        stats: {
          totalProjects: 0,
          completedProjects: 0,
          cancelledProjects: 0,
          totalAppointments: 0,
          totalInteractions: 0,
          openTasksCount: 0,
          lastInteractionAt: null,
          lastProjectDate: null,
          lastPaymentDate: null,
          leadScore: 0,
          satisfactionScore: 0,
          referralCount: 0,
        },
        segmentation: {
          tags: data.segmentation?.tags || [],
          segments: data.segmentation?.segments || [],
          priority: data.segmentation?.priority || "medium",
          isVIP: false,
          isRepeatClient: false,
        },
        consent: data.consent || {},
        notesSummary: data.notesSummary || "",
        isArchived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
        updatedBy: data.createdBy || "admin",
      };

      const docRef = await addDoc(this.getClientsCollection(), clientData);
      const newDoc = await getDoc(docRef);
      return { ...this.convertDocData(newDoc.data()), id: newDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateClient(id, data) {
    try {
      const docRef = doc(db, "clients", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);

      const safeData = { ...data };
      delete safeData.id;
      delete safeData.clientCode;
      delete safeData.createdAt;
      delete safeData.createdBy;

      await updateDoc(docRef, { ...safeData, updatedAt: serverTimestamp() });
      const updatedDoc = await getDoc(docRef);
      return { ...this.convertDocData(updatedDoc.data()), id: updatedDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteClient(id) {
    try {
      const docRef = doc(db, "clients", id);
      await updateDoc(docRef, {
        isArchived: true,
        updatedAt: serverTimestamp(),
      });
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async archiveClient(id) {
    try {
      return await this.updateClient(id, { status: "archived" });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ LEADS ============
  getLeadsCollection() {
    return collection(db, "leads");
  }

  async getLeads(filters = {}) {
    try {
      let q = query(this.getLeadsCollection(), orderBy("createdAt", "desc"));
      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }
      const snapshot = await getDocs(q);
      let leads = [];
      snapshot.forEach((docSnap) => {
        leads.push({ ...this.convertDocData(docSnap.data()), id: docSnap.id });
      });
      leads = leads.filter((l) => l.isArchived !== true);

      if (filters.search) {
        const s = filters.search.toLowerCase();
        leads = leads.filter(
          (l) =>
            l.fullName?.toLowerCase().includes(s) ||
            l.email?.toLowerCase().includes(s) ||
            l.phone?.toLowerCase().includes(s),
        );
      }
      return leads;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getLeadById(id) {
    try {
      const docRef = doc(db, "leads", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);
      return { ...this.convertDocData(docSnap.data()), id: docSnap.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createLead(data) {
    try {
      const errors = validateLeadData(data);
      if (Object.keys(errors).length > 0)
        throw new Error(Object.values(errors)[0]);

      const leadCode = generateLeadCode();
      const leadData = {
        leadCode,
        fullName:
          data.fullName ||
          `${data.firstName || ""} ${data.lastName || ""}`.trim(),
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        email: data.email || "",
        phone: data.phone || "",
        whatsapp: data.whatsapp || data.phone || "",
        leadType: data.leadType || "wedding",
        inquiryType: data.inquiryType || "website",
        source: data.source || {},
        requirements: data.requirements || {},
        message: data.message || "",
        status: data.status || "new",
        pipelineStage: data.pipelineStage || "new",
        priority: data.priority || "medium",
        temperature: data.temperature || "warm",
        ownership: data.ownership || {},
        scores: data.scores || {},
        conversion: {
          isConverted: false,
          convertedClientId: null,
          convertedProjectId: null,
          convertedAt: null,
          lostReason: null,
        },
        activity: {
          interactionCount: 0,
          appointmentCount: 0,
          quotationCount: 0,
          lastContactedAt: null,
          nextFollowUpAt: null,
        },
        tags: data.tags || [],
        isArchived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
      };

      const docRef = await addDoc(this.getLeadsCollection(), leadData);
      const newDoc = await getDoc(docRef);
      return { ...this.convertDocData(newDoc.data()), id: newDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateLead(id, data) {
    try {
      const docRef = doc(db, "leads", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);

      const safeData = { ...data };
      delete safeData.id;
      delete safeData.leadCode;
      delete safeData.createdAt;
      delete safeData.createdBy;

      await updateDoc(docRef, { ...safeData, updatedAt: serverTimestamp() });
      const updatedDoc = await getDoc(docRef);
      return { ...this.convertDocData(updatedDoc.data()), id: updatedDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteLead(id) {
    try {
      const docRef = doc(db, "leads", id);
      await updateDoc(docRef, {
        isArchived: true,
        updatedAt: serverTimestamp(),
      });
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async convertLeadToClient(leadId, clientId) {
    try {
      await this.updateLead(leadId, {
        status: "won",
        conversion: {
          isConverted: true,
          convertedClientId: clientId,
          convertedAt: serverTimestamp(),
        },
      });
      return await this.getClientById(clientId);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ PROJECTS ============
  getProjectsCollection() {
    return collection(db, "projects");
  }

  async getProjects(filters = {}) {
    try {
      let q = query(this.getProjectsCollection(), orderBy("createdAt", "desc"));
      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }
      const snapshot = await getDocs(q);
      let projects = [];
      snapshot.forEach((docSnap) => {
        projects.push({
          ...this.convertDocData(docSnap.data()),
          id: docSnap.id,
        });
      });
      projects = projects.filter((p) => p.isArchived !== true);

      if (filters.search) {
        const s = filters.search.toLowerCase();
        projects = projects.filter(
          (p) =>
            p.title?.toLowerCase().includes(s) ||
            p.projectCode?.toLowerCase().includes(s),
        );
      }
      return projects;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getProjectById(id) {
    try {
      const docRef = doc(db, "projects", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);
      return { ...this.convertDocData(docSnap.data()), id: docSnap.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createProject(data) {
    try {
      const errors = validateProjectData(data);
      if (Object.keys(errors).length > 0)
        throw new Error(Object.values(errors)[0]);

      const projectCode = generateProjectCode();
      const projectData = {
        projectCode,
        clientId: data.clientId,
        leadId: data.leadId || "",
        quotationId: data.quotationId || "",
        contractId: data.contractId || "",
        title: data.title,
        type: data.type || "wedding",
        category: data.category || "standard",
        status: data.status || "enquiry",
        stage: data.stage || "pre_production",
        event: data.event || {},
        package: data.package || {},
        team: data.team || {},
        financials: {
          subtotal: data.financials?.subtotal || 0,
          discount: data.financials?.discount || 0,
          tax: data.financials?.tax || 0,
          grandTotal: data.financials?.grandTotal || 0,
          paid: data.financials?.paid || 0,
          pending: data.financials?.pending || 0,
          currency: "INR",
        },
        workflow: {
          currentMilestone: "pre_production",
          completionPercent: 0,
          isDelayed: false,
          nextDeadline: null,
        },
        deliverables: data.deliverables || {},
        linkedAssets: data.linkedAssets || {},
        clientSnapshot: data.clientSnapshot || {},
        notesSummary: data.notesSummary || "",
        isArchived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
      };

      const docRef = await addDoc(this.getProjectsCollection(), projectData);
      const newDoc = await getDoc(docRef);
      return { ...this.convertDocData(newDoc.data()), id: newDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateProject(id, data) {
    try {
      const docRef = doc(db, "projects", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);

      const safeData = { ...data };
      delete safeData.id;
      delete safeData.projectCode;
      delete safeData.createdAt;
      delete safeData.createdBy;

      await updateDoc(docRef, { ...safeData, updatedAt: serverTimestamp() });
      const updatedDoc = await getDoc(docRef);
      return { ...this.convertDocData(updatedDoc.data()), id: updatedDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteProject(id) {
    try {
      const docRef = doc(db, "projects", id);
      await updateDoc(docRef, {
        isArchived: true,
        updatedAt: serverTimestamp(),
      });
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ QUOTATIONS ============
  getQuotationsCollection() {
    return collection(db, "quotations");
  }

  async createQuotation(data) {
    try {
      const quotationNumber = generateQuotationNumber();
      const quotationData = {
        quotationNumber,
        leadId: data.leadId || "",
        clientId: data.clientId || "",
        projectId: null,
        status: "draft",
        version: 1,
        validUntil: data.validUntil || "",
        items: data.items || [],
        pricing: data.pricing || {},
        share: {
          publicUrl: "",
          pdfUrl: "",
          lastOpenedAt: null,
          acceptedAt: null,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
      };

      const docRef = await addDoc(
        this.getQuotationsCollection(),
        quotationData,
      );
      const newDoc = await getDoc(docRef);
      return { ...this.convertDocData(newDoc.data()), id: newDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateQuotation(id, data) {
    try {
      const docRef = doc(db, "quotations", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);

      const safeData = { ...data };
      delete safeData.id;
      delete safeData.quotationNumber;
      delete safeData.createdAt;
      delete safeData.createdBy;

      await updateDoc(docRef, { ...safeData, updatedAt: serverTimestamp() });
      const updatedDoc = await getDoc(docRef);
      return { ...this.convertDocData(updatedDoc.data()), id: updatedDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ TASKS ============
  getTasksCollection() {
    return collection(db, "tasks");
  }

  async getTasks(filters = {}) {
    try {
      let q = query(this.getTasksCollection(), orderBy("dueDate", "asc"));
      const snapshot = await getDocs(q);
      let tasks = [];
      snapshot.forEach((docSnap) => {
        tasks.push({ ...this.convertDocData(docSnap.data()), id: docSnap.id });
      });
      return tasks;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createTask(data) {
    try {
      const taskData = {
        clientId: data.clientId || "",
        projectId: data.projectId || "",
        leadId: data.leadId || "",
        title: data.title,
        description: data.description || "",
        type: data.type || "follow_up",
        priority: data.priority || "medium",
        status: "pending",
        dueDate: data.dueDate || null,
        assignedTo: data.assignedTo || "",
        completedAt: null,
        notes: data.notes || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
      };

      const docRef = await addDoc(this.getTasksCollection(), taskData);
      const newDoc = await getDoc(docRef);
      return { ...this.convertDocData(newDoc.data()), id: newDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateTask(id, data) {
    try {
      const docRef = doc(db, "tasks", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);

      const safeData = { ...data };
      delete safeData.id;
      delete safeData.createdAt;
      delete safeData.createdBy;

      await updateDoc(docRef, { ...safeData, updatedAt: serverTimestamp() });
      const updatedDoc = await getDoc(docRef);
      return { ...this.convertDocData(updatedDoc.data()), id: updatedDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async completeTask(id) {
    try {
      return await this.updateTask(id, {
        status: "completed",
        completedAt: serverTimestamp(),
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteTask(id) {
    try {
      await deleteDoc(doc(db, "tasks", id));
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ INTERACTIONS ============
  getInteractionsCollection() {
    return collection(db, "interactions");
  }

  async createInteraction(data) {
    try {
      const interactionData = {
        clientId: data.clientId || "",
        leadId: data.leadId || "",
        projectId: data.projectId || "",
        type: data.type || "call",
        direction: data.direction || "outgoing",
        title: data.title || "",
        description: data.description || "",
        date: data.date || serverTimestamp(),
        duration: data.duration || 0,
        assignedTo: data.assignedTo || "",
        createdBy: data.createdBy || "admin",
        followUpDate: data.followUpDate || null,
        followUpCompleted: false,
        notes: data.notes || "",
        attachments: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(
        this.getInteractionsCollection(),
        interactionData,
      );
      const newDoc = await getDoc(docRef);
      return { ...this.convertDocData(newDoc.data()), id: newDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ UTILS ============
  convertTimestamp(value) {
    if (!value) return null;
    if (typeof value === "object" && value.seconds !== undefined) {
      return new Date(
        value.seconds * 1000 + (value.nanoseconds || 0) / 1000000,
      ).toISOString();
    }
    if (value instanceof Date) return value.toISOString();
    return value;
  }

  convertDocData(docData) {
    if (!docData) return docData;
    const converted = { ...docData };
    ["createdAt", "updatedAt", "signedDate", "eventDate", "dueDate"].forEach(
      (field) => {
        if (converted[field])
          converted[field] = this.convertTimestamp(converted[field]);
      },
    );
    return converted;
  }

  handleError(error) {
    console.error("Firebase error:", error);
    if (error.code === "permission-denied") {
      return new Error("You don't have permission to perform this action.");
    }
    return new Error(error.message || "An error occurred");
  }
}

const crmService = new CRMService();
export default crmService;
